import Close from '@mui/icons-material/Close';
import ContentCopy from '@mui/icons-material/ContentCopy';
import InsightsOutlined from '@mui/icons-material/InsightsOutlined';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import { alpha } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { PrimaryButton } from 'components/forms/atoms/FormButtons';
import { type FC, useState } from 'react';
import {
  type AnalysisResult,
  type ProposedChange,
  type ProposedChangesResult,
  useAnalyzePromptMutation,
  useProposeChangesMutation,
} from 'services/api/playground.api';
import {
  GLASS_BACKGROUND,
  GLASS_BLUR,
  NAVY,
  RADIUS_CARD,
  RADIUS_ITEM,
  STATUS_GOOD,
  SURFACE_CONTAINER_LOW,
} from 'styles/tokens';

const ERROR_RED = '#E7233B';

interface AnalysisPanelProps {
  open: boolean;
  onClose: () => void;
  prompt: string;
  conversationJson: string;
  functionsJson: string;
  onApplyImprovedPrompt: (improved: string) => void;
}

/** Replace every occurrence (the old page used first-match String.replace, which mis-applied repeats). */
const applyChanges = (basePrompt: string, changes: ProposedChange[]): string =>
  changes.reduce((acc, change) => {
    if (change.textToRemove && change.textToAdd) {
      return acc.split(change.textToRemove).join(change.textToAdd);
    }
    if (change.textToRemove) {
      return acc.split(change.textToRemove).join('');
    }
    if (change.textToAdd) {
      return `${acc}\n${change.textToAdd}`;
    }
    return acc;
  }, basePrompt);

const sectionLabel = {
  fontFamily: 'Jost, sans-serif',
  fontWeight: 700,
  fontSize: 11,
  textTransform: 'uppercase' as const,
  letterSpacing: '0.05em',
  color: alpha(NAVY, 0.5),
};

const AnalysisPanel: FC<AnalysisPanelProps> = ({
  open,
  onClose,
  prompt,
  conversationJson,
  functionsJson,
  onApplyImprovedPrompt,
}) => {
  const [analysis, setAnalysis] = useState<AnalysisResult | null>(null);
  const [changes, setChanges] = useState<ProposedChangesResult | null>(null);
  const [analyzePrompt, { isLoading: analyzing }] = useAnalyzePromptMutation();
  const [proposeChanges, { isLoading: proposing }] = useProposeChangesMutation();

  const runAnalysis = () => {
    if (!prompt.trim()) {
      return;
    }
    const payload = {
      prompt: `Prompt: ${prompt}.\n\nReference Conversation: ${conversationJson}\n\nAvailable Tools: ${functionsJson}`,
    };
    analyzePrompt(payload)
      .unwrap()
      .then(setAnalysis)
      .catch(() => {});
    proposeChanges(payload)
      .unwrap()
      .then(setChanges)
      .catch(() => {});
  };

  const proposed = changes?.proposedChanges ?? [];
  const improvedPrompt = applyChanges(prompt, proposed);

  const renderAnalysis = () => {
    if (!analysis) {
      return null;
    }
    const { analysis: a } = analysis;
    const structured = a && typeof a === 'object' && !('raw' in a && a.raw);
    if (!structured) {
      return (
        <Typography sx={{ fontFamily: 'Jost, sans-serif', fontSize: 13, color: NAVY, whiteSpace: 'pre-wrap' }}>
          {analysis.rawResponse || JSON.stringify(a, null, 2)}
        </Typography>
      );
    }
    return Object.entries(a).map(([key, value]) => (
      <Box key={key} sx={{ mb: 1 }}>
        <Typography sx={sectionLabel}>{key}</Typography>
        <Typography sx={{ fontFamily: 'Jost, sans-serif', fontSize: 13, color: NAVY }}>
          {Array.isArray(value)
            ? value.map((v) => (typeof v === 'object' ? JSON.stringify(v) : v)).join('. ')
            : String(value)}
        </Typography>
      </Box>
    ));
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      slotProps={{
        paper: {
          sx: {
            width: 'min(460px, 100%)',
            background: GLASS_BACKGROUND,
            backdropFilter: GLASS_BLUR,
            p: 2.5,
          },
        },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <InsightsOutlined sx={{ fontSize: 20, color: NAVY }} />
          <Typography sx={{ fontFamily: 'Jost, sans-serif', fontWeight: 700, fontSize: 16, color: NAVY }}>
            Análisis del prompt
          </Typography>
        </Box>
        <IconButton size="small" onClick={onClose} sx={{ color: alpha(NAVY, 0.6) }}>
          <Close sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <PrimaryButton
          size="small"
          onClick={runAnalysis}
          disabled={!prompt.trim() || analyzing || proposing}
          startIcon={analyzing || proposing ? <CircularProgress size={14} sx={{ color: NAVY }} /> : undefined}
          sx={{ py: 0.6, px: 2 }}
        >
          Analizar
        </PrimaryButton>
        {analysis && (
          <Typography sx={{ fontSize: 11.5, color: alpha(NAVY, 0.5), fontFamily: 'Jost, sans-serif' }}>
            Análisis {analysis.cost} · Cambios {changes?.cost ?? '—'}
          </Typography>
        )}
      </Box>

      <Box sx={{ overflowY: 'auto', flex: 1 }}>
        {renderAnalysis()}

        {proposed.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography sx={{ ...sectionLabel, mb: 1 }}>Cambios propuestos ({proposed.length})</Typography>
            {proposed.map((change, index) => (
              <Box
                key={`${index + 1}-${change.description.slice(0, 16)}`}
                sx={{ bgcolor: SURFACE_CONTAINER_LOW, borderRadius: RADIUS_ITEM, p: 1.25, mb: 1 }}
              >
                <Typography sx={{ fontFamily: 'Jost, sans-serif', fontSize: 12.5, color: NAVY, mb: 0.75 }}>
                  <strong>{index + 1}.</strong> {change.description}
                </Typography>
                {change.textToRemove && (
                  <Box sx={{ bgcolor: alpha(ERROR_RED, 0.08), borderRadius: 1, p: 0.75, mb: 0.5 }}>
                    <Typography
                      sx={{
                        fontFamily: 'ui-monospace, monospace',
                        fontSize: 11.5,
                        color: ERROR_RED,
                        whiteSpace: 'pre-wrap',
                      }}
                    >
                      − {change.textToRemove}
                    </Typography>
                  </Box>
                )}
                {change.textToAdd && (
                  <Box sx={{ bgcolor: alpha(STATUS_GOOD, 0.1), borderRadius: 1, p: 0.75 }}>
                    <Typography
                      sx={{
                        fontFamily: 'ui-monospace, monospace',
                        fontSize: 11.5,
                        color: STATUS_GOOD,
                        whiteSpace: 'pre-wrap',
                      }}
                    >
                      + {change.textToAdd}
                    </Typography>
                  </Box>
                )}
              </Box>
            ))}

            <Box sx={{ bgcolor: SURFACE_CONTAINER_LOW, borderRadius: RADIUS_CARD, p: 1.5, mt: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography sx={{ ...sectionLabel }}>Prompt mejorado</Typography>
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  <Button
                    size="small"
                    startIcon={<ContentCopy sx={{ fontSize: 14 }} />}
                    onClick={() => {
                      navigator.clipboard.writeText(improvedPrompt).catch(() => {});
                    }}
                    sx={{ textTransform: 'none', fontFamily: 'Jost, sans-serif', fontSize: 12, color: NAVY }}
                  >
                    Copiar
                  </Button>
                  <Button
                    size="small"
                    onClick={() => onApplyImprovedPrompt(improvedPrompt)}
                    sx={{
                      textTransform: 'none',
                      fontFamily: 'Jost, sans-serif',
                      fontSize: 12,
                      color: NAVY,
                      fontWeight: 700,
                    }}
                  >
                    Aplicar
                  </Button>
                </Box>
              </Box>
              <Typography
                component="pre"
                sx={{
                  m: 0,
                  fontFamily: 'ui-monospace, monospace',
                  fontSize: 11.5,
                  lineHeight: 1.5,
                  color: NAVY,
                  whiteSpace: 'pre-wrap',
                  maxHeight: 240,
                  overflowY: 'auto',
                }}
              >
                {improvedPrompt}
              </Typography>
            </Box>
          </Box>
        )}
      </Box>
    </Drawer>
  );
};

export default AnalysisPanel;
